import { useState } from 'react';

interface ContactSectionProps {
  onWhatsAppClick: () => void;
}

export default function ContactSection({ onWhatsAppClick }: ContactSectionProps) {
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: '',
    tipoEvento: '',
    dataEvento: '',
    localEvento: '',
    numeroConvidados: '',
    observacoes: '',
    proximosEventos: false,
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
      return isValidType && isValidSize;
    });
    
    if (validFiles.length !== files.length) {
      alert('Apenas imagens até 5MB são permitidas.');
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles].slice(0, 5)); // Máximo 5 fotos
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      // Adicionar fotos ao formulário
      selectedFiles.forEach((file, index) => {
        formDataToSend.append(`foto_${index + 1}`, file);
      });

      const response = await fetch('https://readdy.ai/api/form/d45par6j3hkp5gme3rbg', {
        method: 'POST',
        body: formDataToSend,
      });

      if (response.ok) {
        setSubmitMessage('Mensagem enviada com sucesso! Entraremos em contato em breve.');
        setFormData({
          nome: '',
          telefone: '',
          email: '',
          tipoEvento: '',
          dataEvento: '',
          localEvento: '',
          numeroConvidados: '',
          observacoes: '',
          proximosEventos: false,
        });
        setSelectedFiles([]);
      } else {
        setSubmitMessage('Erro ao enviar mensagem. Tente novamente.');
      }
    } catch (error) {
      setSubmitMessage('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contacts = [
    {
      icon: 'ri-whatsapp-line',
      title: 'WhatsApp',
      info: '(47) 8488-3371',
      color: 'bg-green-500',
      action: onWhatsAppClick,
      buttonText: 'Falar com DJ Jean',
      buttonColor: 'bg-green-500 hover:bg-green-600',
    },
    {
      icon: 'ri-instagram-line',
      title: 'Instagram',
      info: '@djjean_2013',
      color: 'bg-pink-500',
      action: () => window.open('https://www.instagram.com/djjean_2013/', '_blank'),
      buttonText: 'Seguir',
      buttonColor: 'bg-pink-500 hover:bg-pink-600',
    },
    {
      icon: 'ri-facebook-line',
      title: 'Facebook',
      info: 'DJ Jean',
      color: 'bg-blue-600',
      action: () => window.open('https://www.facebook.com/profile.php?id=100078506857361', '_blank'),
      buttonText: 'Curtir',
      buttonColor: 'bg-blue-600 hover:bg-blue-700',
    },
    {
      icon: 'ri-mail-line',
      title: 'E-mail',
      info: 'need.informatica@yahoo.com.br',
      color: 'bg-red-500',
      action: () => window.open('mailto:need.informatica@yahoo.com.br', '_blank'),
      buttonText: 'Enviar',
      buttonColor: 'bg-red-500 hover:bg-red-600',
    },
  ];

  return (
    <section id="contato" className="py-16 bg-gradient-to-br from-gray-800 to-gray-900">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Entre em Contato</h2>
          <p className="text-base md:text-lg text-gray-300">
            Estamos prontos para fazer seu evento ser inesquecível!
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-bold text-white mb-6">Solicite seu Orçamento</h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="nome" className="block text-white text-sm font-medium mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Seu nome completo"
                  />
                </div>
                <div>
                  <label htmlFor="telefone" className="block text-white text-sm font-medium mb-2">
                    Telefone/WhatsApp *
                  </label>
                  <input
                    type="tel"
                    id="telefone"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="(47) 99999-9999"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="email" className="block text-white text-sm font-medium mb-2">
                  E-mail
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="seu@email.com"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="tipoEvento" className="block text-white text-sm font-medium mb-2">
                    Tipo de Evento *
                  </label>
                  <select
                    id="tipoEvento"
                    name="tipoEvento"
                    value={formData.tipoEvento}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                  >
                    <option value="">Selecione o tipo</option>
                    <option value="casamento">Casamento</option>
                    <option value="aniversario">Aniversário</option>
                    <option value="debutante">Debutante</option>
                    <option value="corporativo">Evento Corporativo</option>
                    <option value="festa">Festa em Geral</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="dataEvento" className="block text-white text-sm font-medium mb-2">
                    Data do Evento
                  </label>
                  <input
                    type="date"
                    id="dataEvento"
                    name="dataEvento"
                    value={formData.dataEvento}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="localEvento" className="block text-white text-sm font-medium mb-2">
                    Local do Evento
                  </label>
                  <input
                    type="text"
                    id="localEvento"
                    name="localEvento"
                    value={formData.localEvento}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Cidade ou endereço"
                  />
                </div>
                <div>
                  <label htmlFor="numeroConvidados" className="block text-white text-sm font-medium mb-2">
                    Número de Convidados
                  </label>
                  <input
                    type="number"
                    id="numeroConvidados"
                    name="numeroConvidados"
                    value={formData.numeroConvidados}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Aproximadamente"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="observacoes" className="block text-white text-sm font-medium mb-2">
                  Observações
                </label>
                <textarea
                  id="observacoes"
                  name="observacoes"
                  value={formData.observacoes}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Conte-nos mais sobre seu evento, preferências musicais, horários, etc. (máximo 500 caracteres)"
                ></textarea>
                <div className="text-right text-white/60 text-xs mt-1">
                  {formData.observacoes.length}/500 caracteres
                </div>
              </div>

              {/* Campo para marcar próximos eventos */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="proximosEventos"
                  name="proximosEventos"
                  checked={formData.proximosEventos}
                  onChange={handleInputChange}
                  className="mt-1 w-4 h-4 text-blue-500 bg-white/10 border border-white/20 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label htmlFor="proximosEventos" className="text-white text-sm cursor-pointer">
                  <span className="font-medium">Quero receber avisos sobre próximos eventos</span>
                  <p className="text-white/70 text-xs mt-1">
                    Receba notificações sobre festas, shows e eventos especiais do DJ Jean
                  </p>
                </label>
              </div>

              {/* Campo para Upload de Fotos */}
              <div>
                <label htmlFor="fotos" className="block text-white text-sm font-medium mb-2">
                  Fotos do Local/Evento (Opcional)
                </label>
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type="file"
                      id="fotos"
                      name="fotos"
                      multiple
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label
                      htmlFor="fotos"
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border-2 border-dashed border-white/30 text-white/70 hover:border-white/50 hover:bg-white/15 transition-all cursor-pointer flex items-center justify-center gap-2"
                    >
                      <i className="ri-camera-line text-lg"></i>
                      <span>Clique para adicionar fotos (máx. 5 fotos, 5MB cada)</span>
                    </label>
                  </div>
                  
                  {/* Preview das fotos selecionadas */}
                  {selectedFiles.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="relative group">
                          <div className="aspect-square bg-white/10 rounded-lg overflow-hidden">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs transition-colors cursor-pointer"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                          <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 rounded-b-lg truncate">
                            {file.name}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="text-white/60 text-xs">
                    <p>• Formatos aceitos: JPG, PNG, GIF</p>
                    <p>• Tamanho máximo: 5MB por foto</p>
                    <p>• Máximo: 5 fotos</p>
                  </div>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-gray-500 disabled:to-gray-600 text-white px-6 py-3 rounded-lg font-bold transition-all transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Enviando...' : 'Solicitar Orçamento'}
              </button>
              
              {submitMessage && (
                <div className={`text-center p-3 rounded-lg ${submitMessage.includes('sucesso') ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                  {submitMessage}
                </div>
              )}
            </form>
          </div>
          
          <div>
            <h3 className="text-2xl font-bold text-white mb-6">Contato Direto</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contacts.map((contact, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center">
                  <div className={`w-12 h-12 ${contact.color} rounded-full flex items-center justify-center mx-auto mb-3`}>
                    <i className={`${contact.icon} text-white text-lg`}></i>
                  </div>
                  <h4 className="text-white font-bold mb-2 text-sm">{contact.title}</h4>
                  <p className="text-gray-300 mb-3 text-sm">{contact.info}</p>
                  <button 
                    onClick={contact.action}
                    className={`${contact.buttonColor} text-white px-3 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap text-xs`}
                  >
                    {contact.buttonText}
                  </button>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-10">
              <button 
                onClick={onWhatsAppClick}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-3 rounded-full text-base font-bold transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap flex items-center mx-auto"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                Falar com DJ Jean
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
